import { Button, Icons, useToggle } from '@commenda/kakashi';
import { TStepGroup, TWorkflowStepDefinition, WorkflowStepType } from '@commenda/types';
import { classNames } from '@commenda/utils';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { WorkflowNodeHelper } from '../../workflow/helpers/node';
import { useWorkflow } from '../context/WorkflowContext';
import { AddGroupForm } from './forms/AddGroupForm';
import { UpdateGroupForm } from './forms/UpdateGroupForm';

export const ManageGroups = () => {
  const {
    workflow,
    addStepGroup,
    updateStepGroup,
    removeStepGroup,
    reorderStepGroups,
    updateStepGroups,
  } = useWorkflow();
  const addGroupFormVisibility = useToggle();
  const [groupIdToUpdate, setGroupIdToUpdate] = useState<string | null>(null);

  // Get human and system steps
  const humanAndSystemSteps = useMemo(() => {
    if (!workflow?.steps) return [];
    return workflow.steps.filter(
      (step) =>
        WorkflowNodeHelper.isHumanNode(step.type) ||
        WorkflowNodeHelper.isSystemNode(step.type) ||
        step.type === WorkflowStepType.DEMO_AI_FILLING_AGENT
    );
  }, [workflow?.steps]);

  // Get all step IDs that are assigned to groups
  const assignedStepIds = useMemo(() => {
    if (!workflow?.stepGroups) return new Set<string>();
    return new Set(workflow.stepGroups.flatMap((group) => group.stepIds || []));
  }, [workflow?.stepGroups]);

  // Get unassigned steps
  const unassignedSteps = useMemo(() => {
    return humanAndSystemSteps.filter((step) => !assignedStepIds.has(step.id));
  }, [humanAndSystemSteps, assignedStepIds]);

  const addGroup = (params: {
    name?: string;
    description?: string;
    subworkflowStepId?: string;
    legacyServiceRequestTaskGroupKey?: string;
  }) => {
    addStepGroup({
      id: uuidv4(),
      name: params.name || '',
      description: params.description,
      legacyServiceRequestTaskGroupKey: params.legacyServiceRequestTaskGroupKey,
      stepIds: [],
    });
    addGroupFormVisibility.hide();
  };

  const updateGroup = (
    groupId: string,
    params: {
      name?: string;
      description?: string;
      subworkflowStepId?: string;
      legacyServiceRequestTaskGroupKey?: string;
    }
  ) => {
    updateStepGroup(groupId, params);
    setGroupIdToUpdate(null);
  };

  const deleteGroup = (groupId: string) => {
    removeStepGroup(groupId);
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // If dropped outside a droppable area, do nothing
    if (!destination) return;

    // If dropped in the same position, do nothing
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    // Handle reordering step groups
    if (
      source.droppableId === 'step-groups' &&
      destination.droppableId === 'step-groups'
    ) {
      const newGroups = Array.from(workflow?.stepGroups || []);
      const [removed] = newGroups.splice(source.index, 1);
      newGroups.splice(destination.index, 0, removed);

      reorderStepGroups(newGroups);
      return;
    }

    // Handle moving steps
    const stepId = draggableId;
    const sourceGroupId =
      source.droppableId === 'unassigned-steps'
        ? null
        : source.droppableId.replace('group-', '');
    const destGroupId =
      destination.droppableId === 'unassigned-steps'
        ? null
        : destination.droppableId.replace('group-', '');

    const updates: { groupId: string; updates: Partial<TStepGroup> }[] = [];

    // Handle reordering within the same group
    if (sourceGroupId && destGroupId && sourceGroupId === destGroupId) {
      const group = workflow?.stepGroups?.find((g) => g.id === sourceGroupId);
      if (group) {
        const newStepIds = [...(group.stepIds || [])];
        const [removed] = newStepIds.splice(source.index, 1);
        newStepIds.splice(destination.index, 0, removed);
        updates.push({
          groupId: sourceGroupId,
          updates: { stepIds: newStepIds },
        });
      }
    } else {
      // Handle moving between different groups or to/from unassigned
      // Remove step from source group
      if (sourceGroupId) {
        const sourceGroup = workflow?.stepGroups?.find(
          (g) => g.id === sourceGroupId
        );
        if (sourceGroup) {
          const newStepIds =
            sourceGroup.stepIds?.filter((id) => id !== stepId) || [];
          updates.push({
            groupId: sourceGroupId,
            updates: { stepIds: newStepIds },
          });
        }
      }

      // Add step to destination group
      if (destGroupId) {
        const destGroup = workflow?.stepGroups?.find(
          (g) => g.id === destGroupId
        );
        if (destGroup) {
          const newStepIds = [...(destGroup.stepIds || [])];
          newStepIds.splice(destination.index, 0, stepId);
          updates.push({
            groupId: destGroupId,
            updates: { stepIds: newStepIds },
          });
        }
      }
    }

    // Update groups atomically
    if (updates.length > 0) {
      updateStepGroups(updates);
    }
  };

  const getStepsForGroup = (groupId: string): TWorkflowStepDefinition[] => {
    const group = workflow?.stepGroups?.find((g) => g.id === groupId);
    if (!group) return [];
    return (
      group.stepIds
        ?.map((stepId) => humanAndSystemSteps.find((s) => s.id === stepId))
        .filter(Boolean) || []
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={addGroupFormVisibility.show}
        >
          <Icons.Plus className="size-4" />
          Add Group
        </Button>
      </div>
      {addGroupFormVisibility.visible && (
        <AddGroupForm
          onGroupAdd={addGroup}
          onCancel={addGroupFormVisibility.hide}
          workflowDefinition={workflow}
        />
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 h-[600px]">
          {/* Left Column - Unassigned Steps */}
          <div className="flex-1 flex flex-col border border-stroke-3 rounded-lg p-4">
            <h3 className="txt-medium-medium mb-4">Unassigned Steps</h3>
            <Droppable droppableId="unassigned-steps">
              {(
                unassignedStepsDroppableProvided: DroppableProvided,
                unassignedStepsDroppableSnapshot
              ) => (
                <div
                  ref={unassignedStepsDroppableProvided.innerRef}
                  {...unassignedStepsDroppableProvided.droppableProps}
                  className={classNames(
                    'flex-1 overflow-y-auto rounded-md p-2',
                    unassignedStepsDroppableSnapshot.isDraggingOver &&
                    'bg-bg-primary-hover'
                  )}
                >
                  {unassignedSteps.length === 0 ? (
                    <div className="text-center text-text-tertiary py-8 txt-small-regular">
                      No unassigned steps
                    </div>
                  ) : (
                    unassignedSteps.map((step, index) => (
                      <Draggable
                        key={step.id}
                        draggableId={step.id}
                        index={index}
                      >
                        {(
                          unassignedStepDraggableProvided: DraggableProvided,
                          unassignedStepDraggableSnapshot
                        ) => (
                          <div
                            ref={unassignedStepDraggableProvided.innerRef}
                            {...unassignedStepDraggableProvided.draggableProps}
                            {...unassignedStepDraggableProvided.dragHandleProps}
                            style={{
                              ...unassignedStepDraggableProvided.draggableProps
                                .style,
                              ...(unassignedStepDraggableSnapshot.isDragging && {
                                margin: 0,
                                marginTop: -100,
                                marginLeft: -280,
                              }),
                            }}
                            className={classNames(
                              'flex items-center gap-2 p-2 rounded-md cursor-grab active:cursor-grabbing',
                              !unassignedStepDraggableSnapshot.isDragging &&
                              'mb-2',
                              unassignedStepDraggableSnapshot.isDragging &&
                              '!mb-0',
                              'bg-white border border-stroke-3',
                              unassignedStepDraggableSnapshot.isDragging &&
                              'shadow-lg ring-1 ring-border-primary'
                            )}
                          >
                            <Icons.Menu01 className="size-4 text-text-tertiary" />
                            <div
                              className={classNames(
                                WorkflowNodeHelper.nodeTemplates[step.type]
                                  ?.bgClassName || 'bg-bg-gray-hover',
                                'rounded-md p-1.5'
                              )}
                            >
                              {WorkflowNodeHelper.getNodeIcon(step.type)}
                            </div>
                            <span className="txt-small-medium flex-1">
                              {step.name}
                            </span>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {unassignedStepsDroppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* Right Column - Step Groups */}
          <div className="flex-1 flex flex-col border border-stroke-3 rounded-lg p-4">
            <h3 className="txt-medium-medium mb-4">Step Groups</h3>
            <Droppable droppableId="step-groups" type="GROUP">
              {(provided: DroppableProvided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classNames(
                    'flex-1 overflow-y-auto rounded-md p-2',
                    snapshot.isDraggingOver && 'bg-bg-primary-hover'
                  )}
                >
                  {!workflow?.stepGroups || workflow.stepGroups.length === 0 ? (
                    <div className="text-center text-text-tertiary py-8 txt-small-regular">
                      No groups yet. Click &quot;Add Group&quot; to create one.
                    </div>
                  ) : (
                    workflow.stepGroups.map((group, groupIndex) => (
                      <Draggable
                        key={group.id}
                        draggableId={`group-${group.id}`}
                        index={groupIndex}
                      >
                        {(groupProvided: DraggableProvided, groupSnapshot) => (
                          <div
                            ref={groupProvided.innerRef}
                            {...groupProvided.draggableProps}
                            {...groupProvided.dragHandleProps}
                            style={{
                              ...groupProvided.draggableProps.style,
                              ...(groupSnapshot.isDragging && {
                                margin: 0,
                                marginTop: -100,
                                marginLeft: -320,
                              }),
                            }}
                            className={classNames(
                              'rounded-md border border-stroke-3 bg-white cursor-grab active:cursor-grabbing',
                              !groupSnapshot.isDragging && 'mb-4',
                              groupSnapshot.isDragging && '!mb-0',
                              groupSnapshot.isDragging &&
                              'shadow-lg ring-border-primary'
                            )}
                          >
                            {/* Group Header */}
                            <div className="flex items-center justify-between p-3 border-b border-stroke-3 ">
                              <div className="flex items-center gap-2 flex-1">
                                <Icons.Menu01 className="size-4 text-text-tertiary" />
                                <div className="flex-1">
                                  <div className="txt-small-bold">
                                    {group.name}
                                  </div>
                                  {group.description && (
                                    <div className="txt-extra-small-regular text-text-tertiary">
                                      {group.description}
                                    </div>
                                  )}
                                  <div className="txt-extra-small-regular text-text-tertiary">
                                    {getStepsForGroup(group.id).length} step(s)
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="tertiary"
                                  size="sm"
                                  onClick={() => setGroupIdToUpdate(group.id)}
                                >
                                  <Icons.Edit01 className="size-4" />
                                </Button>
                                <Button
                                  variant="tertiary"
                                  size="sm"
                                  onClick={() => deleteGroup(group.id)}
                                >
                                  <Icons.Trash01 className="size-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Group Steps */}
                            <Droppable droppableId={`group-${group.id}`}>
                              {(
                                stepProvided: DroppableProvided,
                                stepSnapshot
                              ) => (
                                <div
                                  ref={stepProvided.innerRef}
                                  {...stepProvided.droppableProps}
                                  className={classNames(
                                    'min-h-[50px] p-2',
                                    stepSnapshot.isDraggingOver &&
                                    'bg-bg-primary-hover'
                                  )}
                                >
                                  {getStepsForGroup(group.id).length === 0 ? (
                                    <div className="text-center text-text-tertiary py-4 txt-extra-small-regular">
                                      Drag steps here
                                    </div>
                                  ) : (
                                    getStepsForGroup(group.id).map(
                                      (step, stepIndex) => (
                                        <Draggable
                                          key={step.id}
                                          draggableId={step.id}
                                          index={stepIndex}
                                        >
                                          {(
                                            stepDragProvided: DraggableProvided,
                                            stepDragSnapshot
                                          ) => (
                                            <div
                                              ref={stepDragProvided.innerRef}
                                              {...stepDragProvided.draggableProps}
                                              {...stepDragProvided.dragHandleProps}
                                              style={{
                                                ...stepDragProvided
                                                  .draggableProps.style,
                                                ...(stepDragSnapshot.isDragging && {
                                                  margin: 0,
                                                  marginTop: -100,
                                                  marginLeft: -280,
                                                }),
                                              }}
                                              className={classNames(
                                                'flex items-center gap-2 p-2 rounded-md cursor-grab active:cursor-grabbing',
                                                !stepDragSnapshot.isDragging &&
                                                'mb-2',
                                                stepDragSnapshot.isDragging &&
                                                '!mb-0',
                                                'bg-bg-secondary border border-stroke-3',
                                                stepDragSnapshot.isDragging &&
                                                'shadow-lg ring-1 ring-border-primary'
                                              )}
                                            >
                                              <Icons.Menu01 className="size-4 text-text-tertiary" />
                                              <div
                                                className={classNames(
                                                  WorkflowNodeHelper
                                                    .nodeTemplates[step.type]
                                                    ?.bgClassName ||
                                                  'bg-bg-gray-hover',
                                                  'rounded-md p-1.5'
                                                )}
                                              >
                                                {WorkflowNodeHelper.getNodeIcon(
                                                  step.type
                                                )}
                                              </div>
                                              <span className="txt-small-medium flex-1">
                                                {step.name}
                                              </span>
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    )
                                  )}
                                  {stepProvided.placeholder}
                                </div>
                              )}
                            </Droppable>

                            {/* Update Group Form */}
                            {groupIdToUpdate === group.id && (
                              <div className="p-3 border-t border-stroke-3">
                                <UpdateGroupForm
                                  onGroupUpdate={updateGroup}
                                  onCancel={() => setGroupIdToUpdate(null)}
                                  selectGroup={group}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};
